import { useState, useMemo, useCallback } from 'react';
import { useGetOrganizationById, useGetCrmSegments } from '@tinct/core-api';
import { useGetCurrentWorkspaceId } from '@tinct/core-workspace';
import { CrmProviderType, CrmSegment, ReadCrmIntegration } from '@tinct/core-api-models';

export type CrmProviderOption = {
  provider: CrmProviderType;
  integration: ReadCrmIntegration | null;
  isConnected: boolean;
};

export type UseCrmImportReturn = {
  // Provider selection
  providers: CrmProviderOption[];
  selectedProvider: CrmProviderType | null;
  setSelectedProvider: (provider: CrmProviderType | null) => void;
  selectedProviderOption: CrmProviderOption | null;

  // Connection state
  isLoadingConnection: boolean;
  integrationId: string | null;

  // Segments
  segments: CrmSegment[];
  isLoadingSegments: boolean;
  selectedSegment: CrmSegment | null;
  setSelectedSegment: (segment: CrmSegment | null) => void;
};

const SUPPORTED_PROVIDERS: CrmProviderType[] = ['attio', 'hubspot', 'salesforce'];

export const useCrmImport = (): UseCrmImportReturn => {
  const workspaceId = useGetCurrentWorkspaceId();
  const [selectedProvider, setSelectedProviderRaw] = useState<CrmProviderType | null>(null);
  const [selectedSegment, setSelectedSegment] = useState<CrmSegment | null>(null);

  // Get organization with CRM integrations (shared cache with integration components)
  const { data: organization, isLoading: isLoadingConnection } = useGetOrganizationById(
    {
      id: workspaceId,
      fields: {
        crmIntegrations: {
          fields: {
            id: null,
            providerType: null,
            status: null,
            errorDetails: null,
          },
        },
      },
    },
    {
      enabled: !!workspaceId,
    }
  );

  // Build provider options from supported providers + integrations
  const providers = useMemo<CrmProviderOption[]>(() => {
    return SUPPORTED_PROVIDERS.map((provider) => {
      const integration = organization?.crmIntegrations?.find(
        (i) => i.providerType === provider
      ) ?? null;
      return {
        provider,
        integration,
        isConnected: !!integration,
      };
    });
  }, [organization]);

  // Auto-select first connected provider if none selected
  const effectiveProvider = useMemo(() => {
    if (selectedProvider) return selectedProvider;
    const firstConnected = providers.find((p) => p.isConnected);
    return firstConnected?.provider ?? providers[0]?.provider ?? null;
  }, [selectedProvider, providers]);

  const selectedProviderOption = useMemo(() => {
    return providers.find((p) => p.provider === effectiveProvider) ?? null;
  }, [providers, effectiveProvider]);

  const integrationId = selectedProviderOption?.integration?.id ?? null;

  const setSelectedProvider = useCallback((provider: CrmProviderType | null) => {
    setSelectedProviderRaw(provider);
    setSelectedSegment(null);
  }, []);

  // Fetch segments when selected provider is connected
  const { data: segmentsResponse, isLoading: isLoadingSegments } = useGetCrmSegments(
    {
      integrationId: integrationId!,
      offset: 0,
      limit: 100,
    },
    {
      enabled: !!integrationId,
    }
  );

  const segments = segmentsResponse?.data ?? [];

  return {
    providers,
    selectedProvider: effectiveProvider,
    setSelectedProvider,
    selectedProviderOption,
    isLoadingConnection,
    integrationId,
    segments,
    isLoadingSegments,
    selectedSegment,
    setSelectedSegment,
  };
};
